import { Chart } from "chart.js";
interface LineChartConfig {
  backgroundColor?: string;
  borderColor?: string;
  fill?: boolean;
  label: string;
  data: number[];
}

interface PercentilChartConfig {
  value: number;
  label: string;
  color: string;
}

const lineChartBase64 = (
  config: LineChartConfig[],
  labels: string[],
  percentil: PercentilChartConfig,
  yAxisUnit: string = "",
  line?: PercentilChartConfig,
  isTraffic: boolean = false,
  options?: any,
): Promise<string> => {
  return new Promise((resolve) => {
    // console.log({ config });

    // Detect screen size and adjust accordingly
    const isMobile = window.innerWidth < 768;
    const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;

    const width = isMobile ? 800 : 1000;
    const height = isMobile ? 240 : 300;

    // More aggressive scaling for mobile
    const fontScale = isMobile ? 0.25 : isTablet ? 0.6 : 1;
    const paddingScale = isMobile ? 0.3 : isTablet ? 0.6 : 1;
    const borderScale = isMobile ? 0.4 : isTablet ? 0.7 : 1;
    const pointRadius = isMobile ? 2 : isTablet ? 4 : 6;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    canvas.style.position = "absolute";
    canvas.style.left = "-9999px";

    document.body.appendChild(canvas);

    const ctx = canvas.getContext("2d");

    const defaultColors = [
      "#ED7B00",
      "#007ED7",
      "#00ED7B",
      "#D7007E",
      "#7B007E",
    ];

    const hexToRgba = (hex: string, alpha: number) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    if (ctx) {
      const chart = new Chart(ctx, {
        type: "line",
        data: {
          labels: labels,
          datasets: [
            ...(percentil
              ? [
                  {
                    ...percentil,
                    label: percentil.label ?? "95th Percentil",
                    data: Array(labels.length).fill(percentil.value),
                    fill: false,
                    borderColor: percentil.color ?? "#000",
                    backgroundColor: percentil.color ?? "#000",
                    pointRadius: 0,
                    borderWidth: 4 * borderScale,
                  },
                ]
              : []),
            ...(line
              ? [
                  {
                    ...line,
                    label: line.label ?? "",
                    data: Array(labels.length).fill(line.value),
                    fill: false,
                    borderColor: line.color ?? "#ed0000",
                    backgroundColor: line.color ?? "#ed0000",
                    pointRadius: 0,
                    borderWidth: 4 * borderScale,
                  },
                ]
              : []),
            ...config.map((data, index) => {
              const { label, data: _data, backgroundColor, ...rest } = data;
              const border =
                data.borderColor ?? defaultColors[index % defaultColors.length];
              const bgColor = backgroundColor ? backgroundColor : border;
              return {
                label: label,
                data: _data,
                fill: data.fill ?? false,
                borderColor: border,
                backgroundColor: (context: any) => {
                  const chart = context.chart;
                  const { ctx, chartArea } = chart;
                  if (!chartArea) return border;
                  const gradient = ctx.createLinearGradient(
                    0,
                    chartArea.top,
                    0,
                    chartArea.bottom,
                  );
                  gradient.addColorStop(0, hexToRgba(bgColor, 0.5));
                  gradient.addColorStop(0.5, hexToRgba(bgColor, 0.25));
                  gradient.addColorStop(1, hexToRgba(bgColor, 0.05));
                  return gradient;
                },
                tension: 0.3,
                borderWidth: 4 * borderScale,
                pointRadius: pointRadius,
                ...rest,
              };
            }),
          ],
        },
        options: {
          animation: {
            duration: 0,
          },
          responsive: true,
          maintainAspectRatio: true,
          layout: {
            padding: {
              top: isMobile ? 5 : 10,
              bottom: isMobile ? 5 : 10,
              left: isMobile ? 5 : 10,
              right: isMobile ? 5 : 10,
            },
          },
          plugins: {
            legend: {
              labels: {
                usePointStyle: true,
                pointStyle: "circle",
                padding: Math.round(35 * paddingScale),
                font: {
                  size: Math.round(35 * fontScale),
                  family: "Arial",
                  weight: "bold",
                },
                color: "#000",
                boxWidth: isMobile ? 8 : 12,
                boxHeight: isMobile ? 8 : 12,
              },
            },
          },
          scales: {
            y: {
              min: options?.min !== undefined ? options.min : undefined,
              max: options?.max !== undefined ? options.max : undefined,
              ticks: {
                stepSize: options?.stepSize || undefined,
                precision: 0,
                color: "#3c3e46",
                padding: Math.round(10 * paddingScale),
                font: {
                  size: Math.round(25 * fontScale),
                  weight: "bold",
                },
                callback: function (value) {
                  const n = typeof value === "number" ? value : Number(value);
                  if (
                    options?.yAxisMap &&
                    options.yAxisMap[value] !== undefined
                  ) {
                    return options.yAxisMap[value];
                  }
                  return formatAxisValue(n, yAxisUnit, isTraffic);
                },
              },
              grid: {
                drawTicks: false,
                color: "#3c3e46",
                lineWidth: isMobile ? 0.5 : 1,
              },
              border: {
                dash: [5, 10],
                width: 2 * borderScale,
                color: "#3c3e46",
              },
            },
            x: {
              ticks: {
                color: "#3c3e46",
                font: {
                  size: Math.round(25 * fontScale),
                },
              },
              grid: {
                display: false,
                color: "#3c3e46",
              },
              border: {
                display: false,
              },
            },
          },
        },
      });

      setTimeout(() => {
        const base64 = canvas.toDataURL("image/png");
        chart.destroy();
        document.body.removeChild(canvas);
        resolve(base64);
      }, 0);
    } else {
      document.body.removeChild(canvas);
      resolve("");
    }
  });
};

const formatAxisValue = (value: number, unit: string, traffic: boolean) => {
  if (traffic) {
    return formatBits(value, "bps");
  }
  if (unit) {
    return `${value.toFixed(2)} ${unit}`;
  }
  return value.toString();
};

const formatBits = (value: number, unit: "bps" | "B" = "bps") => {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  const suffixes =
    unit === "bps"
      ? ["", "K", "M", "G", "T", "P"]
      : ["", "k", "M", "G", "T", "P"];
  let v = abs;
  let i = 0;
  while (v >= 1000 && i < suffixes.length - 1) {
    v /= 1000;
    i++;
  }
  return `${sign}${v.toFixed(2)} ${suffixes[i]}${unit}`;
};

export default lineChartBase64;
